// Surface-yaw derivation for placement (?surfaceYaw, 2026-09-07).
//
// View-aligned placement (7dad1e3) orients every wheel at the phone:
// the car's angle is ignored, because the default depth path delivers
// no surface normal at all and the plane path's HitResult.normal is a
// hardcoded placeholder. This module derives a usable HORIZONTAL
// facing normal from what each path actually has:
//
//   depth path — a chord of two extra depth-map samples either side of
//   center. Any two points on a vertical surface project, in XZ, onto
//   that surface's horizontal trace, so the chord's XZ perpendicular
//   IS the surface's yaw — device roll and sample height cancel out.
//
//   plane path — the hit pose's real world normal (the driver computes
//   it for surface classification; placement never saw it).
//
// Both derivations keep the gravity-upright guarantee (output is
// always horizontal and unit-length), always FACE THE CAMERA, and
// reject glancing geometry past the view cap — a depth discontinuity
// at the car's edge reads as an extreme implied angle, and the cap is
// what keeps that failure a quiet fallback instead of a sideways
// wheel. Callers fall back to view-aligned on every null.
//
// Pure — no WebXR runtime. The browser-only sampling lives in
// WebXRDriver, same split as depth-placement.ts.

import * as THREE from 'three'

interface Vec3 {
  x: number
  y: number
  z: number
}

interface Quat {
  x: number
  y: number
  z: number
  w: number
}

/** Horizontal offset of the two extra depth samples, in normalized
 *  view coordinates either side of center (0.5 ∓ offset). Wide enough
 *  that the chord spans real surface at phone-to-car distances, small
 *  enough to stay on the car body at the aim the product asks for. */
export const SURFACE_YAW_SAMPLE_OFFSET_NVC = 0.12

/** A chord shorter than this (in XZ metres) is a dropout or a
 *  degenerate sample pair, not surface data. */
export const MIN_YAW_CHORD_M = 0.04

/** Maximum angle between the derived normal and the view-aligned one.
 *  Past this the geometry is glancing — near-parallel to the surface,
 *  or a depth discontinuity spanning the car's edge — and the noise
 *  swamps the signal. 65° covers every plausible walk-around angle. */
export const MAX_YAW_FROM_VIEW_DEG = 65

const MAX_YAW_FROM_VIEW_COS = Math.cos((MAX_YAW_FROM_VIEW_DEG * Math.PI) / 180)

/** How much of a surface normal must be horizontal for it to carry yaw
 *  information. 0.5 ≈ within 60° of vertical — floors, ceilings and
 *  steep ramps are rejected rather than projected into a wild yaw. */
const MIN_HORIZONTAL_NORMAL_LENGTH = 0.5

/** The view-aligned reference: placement-point → camera, projected to
 *  horizontal. Null when the camera is directly above the point. */
function viewAlignedNormal(cameraPos: Vec3, point: Vec3): Vec3 | null {
  const x = cameraPos.x - point.x
  const z = cameraPos.z - point.z
  const len = Math.hypot(x, z)
  if (!(len > 1e-6)) return null
  return { x: x / len, y: 0, z: z / len }
}

/** Faces `n` toward the camera side of the surface and applies the
 *  view-deviation cap. Shared tail of both derivations. */
function faceAndCap(
  n: Vec3,
  cameraPos: Vec3,
  point: Vec3,
): Vec3 | null {
  const view = viewAlignedNormal(cameraPos, point)
  if (view == null) return null
  const toCameraDot = n.x * view.x + n.z * view.z
  const faced =
    toCameraDot < 0 ? { x: -n.x, y: 0, z: -n.z } : { x: n.x, y: 0, z: n.z }
  const viewDot = faced.x * view.x + faced.z * view.z
  if (viewDot < MAX_YAW_FROM_VIEW_COS) return null
  return faced
}

/**
 * Yaw normal from a depth-map neighbourhood chord.
 *
 * The chord's Y is discarded by construction — only the XZ trace
 * carries yaw, and vertical sample noise must never tilt the wheel.
 */
export function yawNormalFromChord({
  left,
  right,
  cameraPos,
  point,
}: {
  left: Vec3
  right: Vec3
  cameraPos: Vec3
  point: Vec3
}): Vec3 | null {
  const tx = right.x - left.x
  const tz = right.z - left.z
  const chordLen = Math.hypot(tx, tz)
  if (!(chordLen >= MIN_YAW_CHORD_M)) return null
  // XZ perpendicular of the tangent; sign settled by faceAndCap.
  const n = { x: tz / chordLen, y: 0, z: -tx / chordLen }
  return faceAndCap(n, cameraPos, point)
}

/**
 * Yaw normal from a real surface normal (the plane path).
 *
 * Gravity-projects the normal, so only wall-ish surfaces qualify — a
 * floor's normal carries no yaw and a steep ramp's projection would be
 * mostly noise.
 */
export function yawNormalFromSurfaceNormal({
  normal,
  cameraPos,
  point,
}: {
  normal: Vec3
  cameraPos: Vec3
  point: Vec3
}): Vec3 | null {
  const len = Math.hypot(normal.x, normal.z)
  if (!(len >= MIN_HORIZONTAL_NORMAL_LENGTH)) return null
  const n = { x: normal.x / len, y: 0, z: normal.z / len }
  return faceAndCap(n, cameraPos, point)
}

/**
 * World point for an off-center depth sample.
 *
 * The Depth API reports camera-PLANE depth (the depth-buffer sense),
 * so the sample's view-space point is the ray through that pixel
 * scaled until its forward component equals the sampled depth — the
 * center pixel degenerates to the existing forward × depth contract.
 * Needs the view's projection matrix because an off-center ray depends
 * on the actual frustum, not just the pose.
 */
export function worldPointFromDepthSample({
  nvcX,
  nvcY,
  depthM,
  projectionMatrix,
  viewPosition,
  viewOrientation,
}: {
  nvcX: number
  nvcY: number
  depthM: number
  projectionMatrix: ArrayLike<number>
  viewPosition: Vec3
  viewOrientation: Quat
}): Vec3 | null {
  if (!Number.isFinite(depthM) || depthM <= 0) return null
  const projInv = new THREE.Matrix4()
    .fromArray(Array.from(projectionMatrix))
    .invert()
  // Normalized view coords (y-down) → NDC (y-up); NDC z is arbitrary,
  // the ray direction is what survives the perspective divide.
  const dirView = new THREE.Vector3(2 * nvcX - 1, 1 - 2 * nvcY, 0.5).applyMatrix4(
    projInv,
  )
  if (!(dirView.z < -1e-9)) return null
  const scale = depthM / -dirView.z
  const pointView = dirView.multiplyScalar(scale)
  const world = pointView
    .applyQuaternion(
      new THREE.Quaternion(
        viewOrientation.x,
        viewOrientation.y,
        viewOrientation.z,
        viewOrientation.w,
      ),
    )
    .add(new THREE.Vector3(viewPosition.x, viewPosition.y, viewPosition.z))
  if (
    !Number.isFinite(world.x) ||
    !Number.isFinite(world.y) ||
    !Number.isFinite(world.z)
  ) {
    return null
  }
  return { x: world.x, y: world.y, z: world.z }
}
